import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ClipboardList, Pencil, Trash2, Play, Square, ArrowRight,
  CheckCircle2, XCircle, PauseCircle, Building2, AlertCircle, Package,
  ShieldCheck, Clock, User, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import {
  useTicket, useDeleteTicket, useReleaseTicket, useAdvanceStation,
  useSetTicketStatus, useStartLabor, useStopLabor,
} from '@/hooks/useProduction';
import {
  STATION_LABELS, TICKET_STATUS_LABELS,
  currentStation, routingProgress,
} from '@/types/production';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { TicketStatusBadge, PriorityBadge } from '@/components/production/TicketBadges';
import { TicketFormModal } from '@/components/production/TicketFormModal';
import { ConsumeMaterialModal } from '@/components/production/ConsumeMaterialModal';
import { RecordQCModal } from '@/components/production/RecordQCModal';
import { formatCurrency } from '@/lib/utils';

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'production', 'write') : false;

  const { data: ticket, isLoading, error } = useTicket(id);

  const release = useReleaseTicket();
  const advance = useAdvanceStation();
  const setStatus = useSetTicketStatus();
  const del = useDeleteTicket();
  const startLabor = useStartLabor();
  const stopLabor = useStopLabor();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [consumeOpen, setConsumeOpen] = useState(false);
  const [qcOpen, setQcOpen] = useState(false);

  const activeLabor = useMemo(
    () => ticket?.labor.find((l) => !l.endedAt),
    [ticket?.labor],
  );

  if (isLoading) return <LoaderScreen />;
  if (error || !ticket) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load ticket"
          description={(error as { message?: string })?.message ?? 'Ticket not found.'}
          action={<Link to="/production/tickets"><Button variant="secondary" leftIcon={<ArrowLeft size={14} />}>Back to Tickets</Button></Link>}
        />
      </Card>
    );
  }

  const station = currentStation(ticket);
  const progress = routingProgress(ticket);
  const isLate = ticket.dueDate
    && new Date(ticket.dueDate).getTime() < Date.now()
    && !['complete', 'cancelled'].includes(ticket.status);

  async function doRelease() {
    try { await release.mutateAsync(ticket!.id); toast.success('Released to floor'); }
    catch (err) { toast.error((err as { message?: string }).message ?? 'Release failed.'); }
  }

  async function doAdvance() {
    try { await advance.mutateAsync({ id: ticket!.id }); toast.success('Advanced to next station'); }
    catch (err) { toast.error((err as { message?: string }).message ?? 'Advance failed.'); }
  }

  async function doStartLabor() {
    if (!station) return;
    try { await startLabor.mutateAsync({ id: ticket!.id, station }); toast.success('Labor timer started'); }
    catch (err) { toast.error((err as { message?: string }).message ?? 'Start failed.'); }
  }

  async function doStopLabor() {
    if (!activeLabor) return;
    try {
      await stopLabor.mutateAsync({ id: ticket!.id, eventId: activeLabor.id });
      toast.success('Labor stopped');
    } catch (err) { toast.error((err as { message?: string }).message ?? 'Stop failed.'); }
  }

  async function doBlock() {
    try {
      await setStatus.mutateAsync({ id: ticket!.id, status: 'blocked', reason: 'Manually blocked' });
      toast.success('Ticket blocked');
    } catch (err) { toast.error((err as { message?: string }).message ?? 'Block failed.'); }
  }

  async function doUnblock() {
    try {
      await setStatus.mutateAsync({ id: ticket!.id, status: 'queued' });
      toast.success('Ticket unblocked');
    } catch (err) { toast.error((err as { message?: string }).message ?? 'Unblock failed.'); }
  }

  async function onDelete() {
    try {
      await del.mutateAsync(ticket!.id);
      toast.success(`Deleted ${ticket!.number}`);
      navigate('/production/tickets');
    } catch (err) { toast.error((err as { message?: string }).message ?? 'Delete failed.'); }
  }

  return (
    <div className="space-y-8">
      <Link to="/production/tickets" className="inline-flex items-center gap-2 text-xs text-text-dim hover:text-text-muted">
        <ArrowLeft size={12} />
        <span className="kicker">All Tickets</span>
      </Link>

      {/* Hero */}
      <header className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-5 min-w-0 flex-1">
          <div className="shrink-0 h-16 w-16 rounded-sm bg-walnut-700 border border-brass-400/30 flex items-center justify-center">
            <ClipboardList size={22} className="text-brass-300" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="kicker font-mono">{ticket.number}</span>
              <TicketStatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
              {isLate && <span className="kicker text-danger">Past Due</span>}
            </div>
            <h1 className="font-display text-3xl text-text leading-tight tracking-tight">{ticket.description}</h1>
            <div className="flex items-center gap-3 mt-2 text-xs text-text-muted flex-wrap">
              <span className="tabular">{ticket.quantity} {ticket.unit ?? 'ea'}</span>
              {ticket.clientName && (
                <>
                  <span>·</span>
                  <Link to={`/clients/${ticket.clientId}`} className="inline-flex items-center gap-1 hover:text-brass-300">
                    <Building2 size={11} /> {ticket.clientName}
                  </Link>
                </>
              )}
              {ticket.orderNumber && (
                <>
                  <span>·</span>
                  <Link to={`/orders/${ticket.orderId}`} className="font-mono hover:text-brass-300">{ticket.orderNumber}</Link>
                </>
              )}
            </div>
          </div>
        </div>

        {canWrite && (
          <div className="flex items-center gap-2 shrink-0">
            {ticket.status === 'draft' && (
              <Button leftIcon={<Send size={13} />} onClick={doRelease} loading={release.isPending}>
                Release to Floor
              </Button>
            )}
            {ticket.status === 'blocked' && (
              <Button variant="secondary" leftIcon={<Play size={13} />} onClick={doUnblock} loading={setStatus.isPending}>
                Unblock
              </Button>
            )}
            {['queued', 'in_progress'].includes(ticket.status) && (
              <Button variant="secondary" leftIcon={<PauseCircle size={13} />} onClick={doBlock} loading={setStatus.isPending}>
                Block
              </Button>
            )}
            {!['draft', 'complete', 'cancelled'].includes(ticket.status) && (
              <Button variant="secondary" leftIcon={<Pencil size={13} />} onClick={() => setEditOpen(true)}>
                Edit
              </Button>
            )}
            {ticket.status === 'draft' && (
              <Button variant="ghost" onClick={() => setConfirmDelete(true)} aria-label="Delete">
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        )}
      </header>

      {/* Overall progress bar */}
      <Card>
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="kicker mb-1">Routing Progress</div>
            <div className="text-sm text-text">
              {station ? (
                <>Current: <span className="text-brass-300">{STATION_LABELS[station]}</span></>
              ) : ticket.status === 'complete' ? (
                <span className="text-success">All stations complete</span>
              ) : (
                'Not started'
              )}
            </div>
          </div>
          <span className="font-display text-2xl text-brass-300 tabular">{progress.toFixed(0)}%</span>
        </div>
        <div className="h-2 bg-bg-subtle rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${
              ticket.status === 'complete' ? 'bg-success' : 'bg-brass-400'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Routing steps strip */}
        <div className="mt-5 flex items-start gap-px">
          {ticket.routing.map((step, idx) => {
            const isCurrent = idx === ticket.currentStepIndex && ticket.status !== 'complete';
            const isDone = step.completed;
            return (
              <div key={idx} className="flex-1">
                <div className={`h-1 transition-colors ${
                  isDone ? 'bg-success' : isCurrent ? 'bg-brass-400' : 'bg-border'
                }`} />
                <div className="pt-2.5 px-1">
                  <div className="flex items-center gap-1.5">
                    {isDone && <CheckCircle2 size={10} className="text-success" />}
                    {isCurrent && <div className="h-2 w-2 rounded-full bg-brass-400 animate-pulse" />}
                    <div className={`text-2xs uppercase tracking-ultra ${
                      isDone ? 'text-text-muted' : isCurrent ? 'text-brass-300' : 'text-text-dim'
                    }`}>
                      {STATION_LABELS[step.station]}
                    </div>
                  </div>
                  {step.completedAt && (
                    <div className="text-2xs text-text-dim tabular mt-0.5">
                      {new Date(step.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  )}
                  {step.estimatedMinutes && !step.completedAt && (
                    <div className="text-2xs text-text-dim tabular mt-0.5">
                      ~{step.estimatedMinutes}m
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Operator controls */}
      {canWrite && !['draft', 'complete', 'cancelled'].includes(ticket.status) && station && (
        <Card>
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="kicker mb-1">Station: {STATION_LABELS[station]}</div>
              <div className="text-sm text-text-muted">Operator controls for the current step.</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {activeLabor ? (
              <Button leftIcon={<Square size={13} />} onClick={doStopLabor} loading={stopLabor.isPending}>
                Stop Labor ({activeLabor.operatorName ?? 'Active'})
              </Button>
            ) : (
              <Button leftIcon={<Play size={13} />} onClick={doStartLabor} loading={startLabor.isPending}>
                Start Labor
              </Button>
            )}

            <Button variant="secondary" leftIcon={<Package size={13} />} onClick={() => setConsumeOpen(true)}>
              Consume Material
            </Button>

            {station === 'qc' && (
              <Button variant="secondary" leftIcon={<ShieldCheck size={13} />} onClick={() => setQcOpen(true)}>
                Record QC Check
              </Button>
            )}

            <Button variant="secondary" rightIcon={<ArrowRight size={13} />} onClick={doAdvance} loading={advance.isPending}>
              Complete &amp; Advance
            </Button>
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Stat label="Status"       value={TICKET_STATUS_LABELS[ticket.status]} />
        <Stat
          label="Labor · Total"
          value={ticket.totalLaborMinutes
            ? `${(ticket.totalLaborMinutes / 60).toFixed(1)}h`
            : '—'}
        />
        <Stat
          label="Materials"
          value={ticket.totalMaterialCost
            ? formatCurrency(ticket.totalMaterialCost)
            : ticket.consumptions?.length
              ? `${ticket.consumptions.length} items`
              : '—'}
        />
        <Stat
          label="Due"
          value={ticket.dueDate
            ? new Date(ticket.dueDate).toLocaleDateString('en-US', { dateStyle: 'medium' })
            : '—'}
          alert={!!isLate}
        />
      </div>

      {/* Body — two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Labor log */}
        <Card className="p-0 overflow-hidden">
          <header className="px-6 py-4 border-b border-border">
            <div className="kicker mb-1">Labor Log · {ticket.labor?.length ?? 0}</div>
            <h3 className="font-display text-base text-text">Time on ticket</h3>
          </header>
          {!ticket.labor?.length ? (
            <div className="p-6 text-sm text-text-dim text-center">No time logged yet.</div>
          ) : (
            <ul className="divide-y divide-border">
              {ticket.labor.slice().reverse().map((l) => (
                <li key={l.id} className="px-6 py-3 flex items-start gap-3">
                  <User size={12} className="text-text-dim mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-text">{l.operatorName ?? 'Operator'}</span>
                      <span className="text-2xs text-text-dim">· {STATION_LABELS[l.station]}</span>
                    </div>
                    <div className="text-2xs text-text-dim tabular mt-0.5">
                      {new Date(l.startedAt).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}
                      {l.endedAt ? ` → ${new Date(l.endedAt).toLocaleTimeString('en-US', { timeStyle: 'short' })}` : ' · running'}
                    </div>
                  </div>
                  <div className="text-sm tabular text-text">
                    {l.endedAt && l.minutes
                      ? `${Math.floor(l.minutes / 60)}h ${Math.round(l.minutes % 60)}m`
                      : <span className="text-brass-300">Active</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Consumptions */}
        <Card className="p-0 overflow-hidden">
          <header className="px-6 py-4 border-b border-border">
            <div className="kicker mb-1">Materials · {ticket.consumptions?.length ?? 0}</div>
            <h3 className="font-display text-base text-text">Consumed</h3>
          </header>
          {!ticket.consumptions?.length ? (
            <div className="p-6 text-sm text-text-dim text-center">No materials consumed yet.</div>
          ) : (
            <ul className="divide-y divide-border">
              {ticket.consumptions.slice().reverse().map((c) => (
                <li key={c.id} className="px-6 py-3">
                  <div className="flex items-baseline justify-between">
                    <Link to={`/inventory/${c.itemId}`} className="font-mono text-2xs text-brass-300">
                      {c.itemSku}
                    </Link>
                    <span className="text-sm tabular text-text">
                      {c.quantity} {c.unit ?? 'ea'}
                    </span>
                  </div>
                  <div className="text-sm text-text truncate">{c.itemName}</div>
                  <div className="text-2xs text-text-dim mt-0.5">
                    {new Date(c.consumedAt).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* QC checks */}
      {ticket.qcChecks?.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <header className="px-6 py-4 border-b border-border">
            <div className="kicker mb-1">Quality Checks · {ticket.qcChecks.length}</div>
            <h3 className="font-display text-base text-text">QC history</h3>
          </header>
          <ul className="divide-y divide-border">
            {ticket.qcChecks.slice().reverse().map((qc) => (
              <li key={qc.id} className="px-6 py-4 flex items-start gap-3">
                <div className={`h-8 w-8 shrink-0 rounded-sm flex items-center justify-center ${
                  qc.outcome === 'pass'   ? 'bg-success/10 border border-success/40' :
                  qc.outcome === 'rework' ? 'bg-warning/10 border border-warning/40' :
                                            'bg-danger/10 border border-danger/40'
                }`}>
                  {qc.outcome === 'pass'
                    ? <CheckCircle2 size={14} className="text-success" />
                    : qc.outcome === 'rework'
                      ? <AlertCircle size={14} className="text-warning" />
                      : <XCircle size={14} className="text-danger" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm text-text capitalize">{qc.outcome}</span>
                    {qc.defectCategory && (
                      <span className="text-2xs text-text-dim">· {qc.defectCategory}</span>
                    )}
                  </div>
                  <div className="text-2xs text-text-dim tabular mt-0.5 flex items-center gap-2">
                    <Clock size={10} />
                    {new Date(qc.checkedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                    {qc.checkedByName && <>· {qc.checkedByName}</>}
                  </div>
                  {qc.notes && (
                    <p className="mt-2 text-xs text-text-muted leading-relaxed">{qc.notes}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Notes */}
      {ticket.notes && (
        <Card>
          <div className="kicker mb-2">Notes</div>
          <p className="text-sm text-text-muted whitespace-pre-wrap leading-relaxed">{ticket.notes}</p>
        </Card>
      )}

      {/* Modals */}
      <TicketFormModal open={editOpen} onClose={() => setEditOpen(false)} ticket={ticket} />
      <ConsumeMaterialModal open={consumeOpen} onClose={() => setConsumeOpen(false)} ticket={ticket} />
      <RecordQCModal open={qcOpen} onClose={() => setQcOpen(false)} ticket={ticket} />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onDelete}
        loading={del.isPending}
        destructive
        title={`Delete ${ticket.number}?`}
        confirmLabel="Delete ticket"
        description="Only draft tickets can be deleted. Once on the floor, cancel instead to preserve history."
      />
    </div>
  );
}

function Stat({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className={`font-display text-lg tabular ${alert ? 'text-danger' : 'text-text'}`}>
        {value}
      </div>
    </div>
  );
}
